import { useState, FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth-context';
import { ApiError } from '../api';
import { LangSwitcher } from '../components/LangSwitcher';
import { useTurnstile } from '../turnstile';

/**
 * The API returns a `code` on every 403 login refusal and on the 400 captcha
 * rejection; anything else (401, network) falls through to the generic message
 * so a wrong password never reveals whether the account exists.
 */
const ERROR_KEY: Record<string, string> = {
  captcha_failed: 'login.captchaFailed',
  account_locked: 'login.locked',
  account_suspended: 'login.accountSuspended',
  school_suspended: 'login.schoolSuspended',
  school_expired: 'login.schoolExpired',
  no_school: 'login.noSchool',
};

export function Login() {
  const { t } = useTranslation();
  const { signIn } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const captcha = useTurnstile();

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(email, password, captcha.token ?? undefined);
      nav('/', { replace: true });
    } catch (err) {
      const key = err instanceof ApiError ? ERROR_KEY[err.message] : undefined;
      setError(t(key ?? 'login.failed'));
      // Turnstile tokens are single-use — spent on the attempt that just
      // failed, whatever the reason. Without a fresh one, the retry would be
      // refused as captcha_failed and the real error would never resurface.
      captcha.reset();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="flex justify-end p-4">
        <LangSwitcher />
      </header>
      <main className="flex-1 flex items-center justify-center px-4">
        <form
          onSubmit={submit}
          className="w-full max-w-sm bg-white rounded-xl shadow-sm border border-slate-200 p-6 space-y-4"
        >
          <h1 className="text-xl font-semibold text-slate-900">{t('login.title')}</h1>

          <div className="space-y-1">
            <label htmlFor="email" className="block text-sm font-medium text-slate-700">
              {t('login.email')}
            </label>
            <input
              id="email"
              type="email"
              required
              autoComplete="username"
              dir="ltr"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-500"
            />
          </div>

          <div className="space-y-1">
            <label htmlFor="password" className="block text-sm font-medium text-slate-700">
              {t('login.password')}
            </label>
            <input
              id="password"
              type="password"
              required
              autoComplete="current-password"
              dir="ltr"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-500"
            />
          </div>

          {/* Managed mode is usually invisible; the div collapses to nothing
              when no site key is configured, so dev builds are unaffected. */}
          <div ref={captcha.ref} className="empty:hidden" />

          {error && (
            <p role="alert" className="text-sm text-red-600 bg-red-50 rounded-md p-2">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full rounded-md bg-sky-600 px-4 py-2 text-white font-medium hover:bg-sky-700 disabled:opacity-50"
          >
            {busy ? t('login.signingIn') : t('login.submit')}
          </button>

          <Link to="/forgot" className="block text-sm text-sky-600 hover:text-sky-700">
            {t('login.forgot')}
          </Link>
        </form>
      </main>
    </div>
  );
}
